import { useDispatch } from 'react-redux';
import { setActivityDrawer } from '@store/appStore';
import { buildCatalogOrigin } from '@api/ActivityLogs/CatalogActivity';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, InputAdornment, Stack, TextField, Typography, Menu, MenuItem, IconButton } from '@mui/material';
import useOpenModal from '@hooks/useOpenModal';
import useConfirmationModal from '@hooks/useConfirmationModal';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import { ModalSaveModifierGroup } from '@screens/Catalog/ModifierGroups/ModalSaveModifierGroup';
import ModifierGroupsService from '@api/ModifierGroups/ModifierGroupCtrl';
import {
    buildRecipesById,
    getModifierGroupMissingRecipeIds,
    ModifierGroup
} from '@api/ModifierGroups/ModifierGroup';
import RecipesService from '@api/Recipes/RecipeCtrl';
import { Recipe } from '@api/Recipes/Recipe';
import InputsService from '@api/Inputs/InputCtrl';
import { Input } from '@api/Inputs/Input';
import Grid from '@mui/material/Grid';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SettingsIcon from '@mui/icons-material/Settings';
import { ResponsiveDataGrid, ResponsiveGridColDef } from '@components/Utilities/Tables/ResponsiveDataGrid';
import { ModalConfirmation } from '@components/Utilities/Modals/ModalConfirmation';

const ListModifierGroups: React.FunctionComponent = () => {
    const modifierGroupsService = useMemo(() => new ModifierGroupsService(), []);
    const recipesService = useMemo(() => new RecipesService(), []);
    const inputsService = useMemo(() => new InputsService(), []);
    const [modifierGroups, setModifierGroups] = useState<ModifierGroup[]>([]);
    const [recipes, setRecipes] = useState<Recipe[]>([]);
    const [inputs, setInputs] = useState<Input[]>([]);
    const [currentModifierGroup, setCurrentModifierGroup] = useState<ModifierGroup | undefined>(undefined);
    const [filters, setFilters] = useState({
        search: ''
    });
    const modalSaveModifierGroup = useOpenModal();
    const modalConfirmation = useConfirmationModal();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedModifierGroup, setSelectedModifierGroup] = useState<ModifierGroup | undefined>(undefined);

    const recipesById = useMemo(() => buildRecipesById(recipes), [recipes]);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: ModifierGroup) => {
        setAnchorEl(event.currentTarget);
        setSelectedModifierGroup(row);
    };

    const dispatch = useDispatch();

    /**
     * Abre la bitácora de este objeto (M2). Antes de esto, el catálogo escribía
     * renglones que **nadie podía ver**: no había puerta en ninguna de sus
     * pantallas.
     *
     * El snapshot se toma ANTES de cerrar el menú, que es donde se pierde la
     * selección, y el renglón de creación sale del propio documento (D8): no
     * cuesta una lectura.
     */
    const handleActivity = () => {
        const item = selectedModifierGroup;
        handleMenuClose();
        if (!item?.id) return;
        dispatch(
            setActivityDrawer({
                isOpen: true,
                section: 'modifier-group',
                sectionId: item.id,
                derived: buildCatalogOrigin(item, 'creó el modificador', 'Modificador creado')
            })
        );
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        handleMenuClose();
        if (selectedModifierGroup) {
            setCurrentModifierGroup(selectedModifierGroup);
            modalSaveModifierGroup.toggle();
        }
    };

    const handleDeleteClick = () => {
        handleMenuClose();
        if (selectedModifierGroup) {
            modalConfirmation.setModalData(
                'Eliminar modificador',
                `¿Estás seguro de que deseas eliminar el modificador "${selectedModifierGroup.name}"? Esta acción no se puede deshacer.`
            );
        }
    };

    const handleDeleteConfirm = async () => {
        if (selectedModifierGroup?.id) {
            showLoader();
            try {
                await modifierGroupsService.deleteModifierGroup(selectedModifierGroup.id);
                showAlertMessage('Modificador eliminado exitosamente');
                await getData();
            } catch (error) {
                showAlertError(error);
            }
            hideLoader();
        }
    };

    const getData = async () => {
        showLoader();
        try {
            const [dataModifierGroups, dataRecipes, dataInputs] = await Promise.all([
                modifierGroupsService.getModifierGroups(),
                recipesService.getRecipes(),
                inputsService.getInputs()
            ]);
            setModifierGroups(dataModifierGroups);
            setRecipes(dataRecipes);
            setInputs(dataInputs);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onOpenModalAddModifierGroup = () => {
        setCurrentModifierGroup(undefined);
        modalSaveModifierGroup.toggle();
    };

    const onSaveModifierGroup = async (modifierGroup: ModifierGroup) => {
        showLoader();
        try {
            const newData = await modifierGroupsService.saveModifierGroup(modifierGroup);
            setCurrentModifierGroup(newData);
            showAlertMessage('Modificador guardado exitosamente');
            await getData();
            modalSaveModifierGroup.toggle();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        void getData();
    }, []);

    const columns = useMemo<ResponsiveGridColDef<ModifierGroup>[]>(
        () => [
            {
                field: 'name',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Modificador
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.name}</Typography>
            },
            {
                field: 'options',
                flex: 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Opciones
                    </Typography>
                ),
                valueGetter: ({ row }) =>
                    (row.options || []).map((option) => option.name).join(', '),
                renderCell: ({ row }) => {
                    const missing = getModifierGroupMissingRecipeIds(row.options, recipesById);
                    const summary = (row.options || [])
                        .map((option) => `${option.name}${option.extraCost > 0 ? ` (+$${option.extraCost})` : ''}`)
                        .join(', ');
                    return (
                        <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
                            <Typography noWrap>{summary || 'Sin opciones'}</Typography>
                            {missing.length > 0 && (
                                <Chip size="small" color="error" label={`${missing.length} receta(s) eliminada(s)`} />
                            )}
                        </Stack>
                    );
                }
            },
            {
                field: 'extras',
                flex: 1 / 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Con costo extra
                    </Typography>
                ),
                valueGetter: ({ row }) => (row.options || []).filter((option) => option.extraCost > 0).length,
                renderCell: ({ row }) => {
                    const extras = (row.options || []).filter((option) => option.extraCost > 0).length;
                    return extras > 0 ? (
                        <Chip size="small" color="primary" label={extras} />
                    ) : (
                        <Typography color="text.secondary">—</Typography>
                    );
                }
            },
            {
                field: 'actions',
                flex: 1 / 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                headerAlign: 'center',
                align: 'center',
                renderHeader: () => <SettingsIcon sx={{ color: '#809FB8' }} />,
                renderCell: ({ row }) => (
                    <IconButton onClick={(e) => handleMenuOpen(e, row)}>
                        <MoreVertIcon />
                    </IconButton>
                )
            }
        ],
        [recipesById]
    );

    return (
        <Stack height="100%" spacing={2}>
            <Box>
                <Grid container spacing={2}>
                    <Grid item xs={6} />
                    <Grid item xs={12} md={3} textAlign="right">
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onOpenModalAddModifierGroup}
                            endIcon={<AddIcon />}
                        >
                            Nuevo modificador
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Buscar modificador"
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>
            <ResponsiveDataGrid
                searchValue={filters.search}
                rows={modifierGroups}
                columns={columns}
                onCellDoubleClick={(cel) => {
                    setCurrentModifierGroup(cel.row as ModifierGroup);
                    modalSaveModifierGroup.toggle();
                }}
            />
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleEdit}>Editar</MenuItem>
                <MenuItem onClick={handleActivity}>Actividad</MenuItem>
                <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                    Eliminar
                </MenuItem>
            </Menu>
            <ModalSaveModifierGroup
                modalSaveModifierGroup={modalSaveModifierGroup}
                modifierGroup={currentModifierGroup}
                recipesList={recipes}
                inputsList={inputs}
                onSaveModifierGroup={onSaveModifierGroup}
            />
            <ModalConfirmation modalConfirmation={modalConfirmation} onConfirmation={handleDeleteConfirm} />
        </Stack>
    );
};
export default ListModifierGroups;
